import {
  AppShortcutOutlined,
  BadgeOutlined,
  ColorLensOutlined,
  Edit,
  HttpOutlined,
  ImageOutlined,
  LandscapeOutlined,
} from "@mui/icons-material";
import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import { AvatarUploader } from "components/Shared/AvatarUploader";
import FormDrawer from "components/Shared/FormDrawer";
import InformationTile from "components/Shared/InformationTile";
import { BASE_CONSTANTS } from "constants/baseConstants";
import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { EntityBrandingForm } from "./EntityBrandingForm";
import {
  useEditEntityMutation,
  useDetailEntityQuery,
} from "store/services/entityApi";
import { useDOUploadFile } from "hooks";
import { useAuth } from "hooks/useAuth";
import { useEntityDetailContext } from "contexts/EntityDetailContext";

export const EntityBrandingTab = () => {
  const { id } = useParams();
  const [showForm, setShowForm] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState(null);

  const { entityDetail } = useEntityDetailContext();
  const { viewEntity } = useAuth();
  const { handleUploadFiles } = useDOUploadFile();

  const [updateBranding, { isLoading: updatingBranding }] =
    useEditEntityMutation();
  // Ensure entity data is refetched after updates
  const { refetch } = useDetailEntityQuery(id);

  const handleClose = () => {
    setShowForm(false);
  };

  const handleUpdatePreferences = async (data) => {
    try {
      await updateBranding({
        ...data,
        id: id,
      }).unwrap();
      toast.success("Entity Preferences successfully updated!");
      refetch(); // Refetch entity data to update UI
      handleClose();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update entity!");
      console.error("handleUpdatePreferences error:", err);
    }
  };

  const logoHandler = async (data) => {
    try {
      const uploadResult = await handleUploadFiles([data.file], {
        folderName: "ENTITY_BRANDING.LOGO",
        entity: viewEntity?.entity?._id,
        account: viewEntity?.entity?.account?._id,
      });

      const uploadedFile = uploadResult.uploadedFiles[0];
      if (!uploadedFile?.url) {
        throw new Error("Failed to upload logo to DigitalOcean Spaces");
      }

      await updateBranding({
        id: id,
        logo: {
          name: data.name,
          publicId: uploadedFile.publicId || null,
          url: uploadedFile.url,
        },
      }).unwrap();
      toast.success("Entity logo successfully updated!");
      refetch(); // Refetch entity data
      handleClose();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update entity logo!");
      console.error("logoHandler error:", err);
    }
  };

  const horizontalLogoHandler = async (data) => {
    try {
      const uploadResult = await handleUploadFiles([data.file], {
        folderName: "ENTITY_BRANDING.LOGO_HORIZONTAL",
        entity: viewEntity?.entity?._id,
        account: viewEntity?.entity?.account?._id,
      });

      const uploadedFile = uploadResult.uploadedFiles[0];
      if (!uploadedFile?.url) {
        throw new Error(
          "Failed to upload horizontal logo to DigitalOcean Spaces"
        );
      }

      await updateBranding({
        id: id,
        logoHorizontal: {
          name: data.name,
          publicId: uploadedFile.publicId || null,
          url: uploadedFile.url,
        },
      }).unwrap();
      toast.success("Entity horizontal logo successfully updated!");
      refetch(); // Refetch entity data
      handleClose();
    } catch (err) {
      toast.error(
        err?.data?.message ?? "Couldn't update entity horizontal logo!"
      );
      console.error("horizontalLogoHandler error:", err);
    }
  };

  const iconHandler = async (data) => {
    try {
      const uploadResult = await handleUploadFiles([data.file], {
        folderName: "ENTITY_BRANDING.ICON",
        entity: viewEntity?.entity?._id,
        account: viewEntity?.entity?.account?._id,
      });

      const uploadedFile = uploadResult.uploadedFiles[0];
      if (!uploadedFile?.url) {
        throw new Error("Failed to upload icon to DigitalOcean Spaces");
      }

      await updateBranding({
        id: id,
        icon: {
          name: data.name,
          publicId: uploadedFile.publicId || null,
          url: uploadedFile.url,
        },
      }).unwrap();
      toast.success("Entity icon successfully updated!");
      refetch(); // Refetch entity data
      handleClose();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update entity icon!");
      console.error("iconHandler error:", err);
    }
  };

  const mobileBannerHandler = async (data) => {
    try {
      const uploadResult = await handleUploadFiles([data.file], {
        folderName: "ENTITY_BRANDING.MOBILE_BANNER",
        entity: viewEntity?.entity?._id,
        account: viewEntity?.entity?.account?._id,
      });

      const uploadedFile = uploadResult.uploadedFiles[0];
      if (!uploadedFile?.url) {
        throw new Error(
          "Failed to upload mobile banner to DigitalOcean Spaces"
        );
      }

      await updateBranding({
        id: id,
        mobileBannerImage: {
          name: data.name,
          publicId: uploadedFile.publicId || null,
          url: uploadedFile.url,
        },
      }).unwrap();
      toast.success("Entity banner successfully updated!");
      refetch(); // Refetch entity data
      handleClose();
    } catch (err) {
      toast.error(
        err?.data?.message ?? "Couldn't update entity mobile banner image!"
      );
      console.error("mobileBannerHandler error:", err);
    }
  };

  const removeAvatarHandler = async () => {
    try {
      await updateBranding({
        id: id,
        [selectedImage]: null,
      }).unwrap();
      toast.success(`Image deleted successfully!`);
      refetch(); // Refetch entity data
      setShowConfirm(false);
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't delete image!");
      console.error("removeAvatarHandler error:", err);
    }
  };

  const imageBoxStyle = (isLogo = false) => ({
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    height: { xs: 150, md: 200 },
    width: { xs: 150, md: 400 },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: isLogo ? "#e0e0e0" : "#ffffff",
    position: "relative",
    overflow: "hidden",
  });

  const imageStyle = {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  };

  return (
    <>
      <Box
        sx={{
          p: BASE_CONSTANTS.PADDING_MD,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          startIcon={<Edit />}
          variant="outlined"
          onClick={() => setShowForm(true)}
        >
          Branding
        </Button>
      </Box>

      <Box padding={BASE_CONSTANTS.PADDING_MD}>
        <Grid container spacing={4}>
          <Grid item md={4}>
            <InformationTile
              icon={() => <HttpOutlined color="primary" />}
              title="Domain"
              subtitle={
                entityDetail?.domain ? (
                  <a
                    href={`https://${entityDetail.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", color: "#1976d2" }}
                  >
                    {entityDetail.domain}
                  </a>
                ) : (
                  "N/A"
                )
              }
            />
          </Grid>

          <Grid item md={4}>
            <InformationTile
              icon={() => <ColorLensOutlined color="primary" />}
              title="Primary Color"
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    backgroundColor: entityDetail?.brandPrimary ?? "#ffffff",
                    borderRadius: 1,
                    marginRight: 1,
                    border: "1px solid #e0e0e0",
                  }}
                />
                <Typography style={{ fontWeight: 400 }}>
                  {entityDetail?.brandPrimary ?? "N/A"}
                </Typography>
              </Box>
            </InformationTile>
          </Grid>
          <Grid item md={4}>
            <InformationTile
              icon={() => <ColorLensOutlined color="primary" />}
              title="Secondary Color"
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    backgroundColor: entityDetail?.brandSecondary ?? "#ffffff",
                    borderRadius: 1,
                    marginRight: 1,
                    border: "1px solid #e0e0e0",
                  }}
                />
                <Typography style={{ fontWeight: 400 }}>
                  {entityDetail?.brandSecondary ?? "N/A"}
                </Typography>
              </Box>
            </InformationTile>
          </Grid>
          <Grid item md={4}>
            <InformationTile
              icon={() => <BadgeOutlined color="primary" />}
              title="Branding Enabled"
              subtitle={entityDetail?.enableBranding ? "Enable" : "Disable"}
            />
          </Grid>
        </Grid>
      </Box>
      <Divider />

      <Grid container spacing={2} padding={3}>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <ImageOutlined sx={{ mr: 1 }} />
            <Typography>Logo</Typography>
          </Box>
          <Typography variant="caption" display="block" sx={{ pt: 1, pb: 2 }}>
            Requirements: 300 x 300 pixels; PNG format
          </Typography>
          <Box sx={imageBoxStyle(true)}>
            <AvatarUploader
              onSubmitAvatar={logoHandler}
              onDeleteAvatar={() => {
                setSelectedImage("logo");
                setShowConfirm(true);
              }}
              avatar={entityDetail?.logo?.url}
              placeholder={
                <ImageOutlined sx={{ fontSize: 40, color: "#757575" }} />
              }
              imageStyle={imageStyle}
              showControlsBelow
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <ImageOutlined sx={{ mr: 1 }} />
            <Typography>Horizontal Logo</Typography>
          </Box>
          <Typography variant="caption" display="block" sx={{ pt: 1, pb: 2 }}>
            Requirements: 300 x 800 pixels; PNG format, preferably white color
          </Typography>
          <Box sx={imageBoxStyle()}>
            <AvatarUploader
              onSubmitAvatar={horizontalLogoHandler}
              onDeleteAvatar={() => {
                setSelectedImage("logoHorizontal");
                setShowConfirm(true);
              }}
              avatar={entityDetail?.logoHorizontal?.url}
              placeholder={
                <LandscapeOutlined sx={{ fontSize: 40, color: "#757575" }} />
              }
              imageStyle={imageStyle}
              showControlsBelow
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <LandscapeOutlined sx={{ mr: 1 }} />
            <Typography>Icon</Typography>
          </Box>
          <Typography variant="caption" display="block" sx={{ pt: 1, pb: 2 }}>
            Requirements: 150 x 150 pixels; PNG format
          </Typography>
          <Box sx={imageBoxStyle()}>
            <AvatarUploader
              onSubmitAvatar={iconHandler}
              onDeleteAvatar={() => {
                setSelectedImage("icon");
                setShowConfirm(true);
              }}
              avatar={entityDetail?.icon?.url}
              placeholder={
                <LandscapeOutlined sx={{ fontSize: 40, color: "#757575" }} />
              }
              imageStyle={imageStyle}
              showControlsBelow
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <AppShortcutOutlined sx={{ mr: 1 }} />
            <Typography>Mobile Banner</Typography>
          </Box>
          <Typography variant="caption" display="block" sx={{ pt: 1, pb: 2 }}>
            Requirements: 800 x 1400 pixels; PNG format
          </Typography>
          <Box sx={imageBoxStyle()}>
            <AvatarUploader
              onSubmitAvatar={mobileBannerHandler}
              onDeleteAvatar={() => {
                setSelectedImage("mobileBannerImage");
                setShowConfirm(true);
              }}
              avatar={entityDetail?.mobileBannerImage?.url}
              placeholder={
                <LandscapeOutlined sx={{ fontSize: 40, color: "#757575" }} />
              }
              imageStyle={imageStyle}
              showControlsBelow
            />
          </Box>
        </Grid>
      </Grid>

      <AppConfirmDialog
        maxWidth="sm"
        open={showConfirm}
        onClose={() => {
          setShowConfirm(false);
          setSelectedImage(null);
        }}
        onConfirm={removeAvatarHandler}
        loading={updatingBranding}
      >
        Are you sure you want to delete this avatar? This action cannot be
        undone!
      </AppConfirmDialog>

      <FormDrawer title="Edit Branding" open={showForm} onClose={handleClose}>
        <EntityBrandingForm
          isBusy={updatingBranding}
          onSubmit={handleUpdatePreferences}
          onCancel={handleClose}
          initialValues={{
            displayName: entityDetail?.displayName || "",
            location: entityDetail?.location || "",
            telNo: entityDetail?.telNo || "",
            brandPrimary: entityDetail?.brandPrimary || "",
            brandSecondary: entityDetail?.brandSecondary || "",
            enableBranding: entityDetail?.enableBranding || false,
            domain: entityDetail?.domain || "",
          }}
        />
      </FormDrawer>
    </>
  );
};
